import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiRequest, { API_URLS } from "../services/api";
import Alert from "../components/Alert";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";

// ─────────────────────────────────────────────────────────────────────────────
// EXCEL STYLE CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const XL = {
  title:        { font: { name: "Arial", sz: 20, bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "0F3460" } }, alignment: { horizontal: "left", vertical: "center" } },
  sectionHead:  { font: { name: "Arial", sz: 11, bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "1E5AA0" } }, alignment: { horizontal: "left", vertical: "center" }, border: { bottom: { style: "medium", color: { rgb: "0A2E5C" } } } },
  colHead:      { font: { name: "Arial", sz: 10, bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "2E75B6" } }, alignment: { horizontal: "center", vertical: "center", wrapText: true }, border: { top: { style: "medium", color: { rgb: "0A2E5C" } }, bottom: { style: "medium", color: { rgb: "0A2E5C" } }, left: { style: "thin", color: { rgb: "0A2E5C" } }, right: { style: "thin", color: { rgb: "0A2E5C" } } } },
  label:        { font: { name: "Arial", sz: 11, bold: true, color: { rgb: "0F3460" } }, fill: { patternType: "solid", fgColor: { rgb: "E8F0FB" } }, alignment: { horizontal: "left", vertical: "center" }, border: { bottom: { style: "thin", color: { rgb: "C5D8F0" } }, top: { style: "thin", color: { rgb: "C5D8F0" } }, left: { style: "thin", color: { rgb: "C5D8F0" } }, right: { style: "thin", color: { rgb: "C5D8F0" } } } },
  value:        { font: { name: "Arial", sz: 11, color: { rgb: "1A1A2E" } }, fill: { patternType: "solid", fgColor: { rgb: "FFFFFF" } }, alignment: { horizontal: "left", vertical: "center" }, border: { bottom: { style: "thin", color: { rgb: "C5D8F0" } }, top: { style: "thin", color: { rgb: "C5D8F0" } }, left: { style: "thin", color: { rgb: "C5D8F0" } }, right: { style: "thin", color: { rgb: "C5D8F0" } } } },
  numValue:     { font: { name: "Arial", sz: 11, bold: true, color: { rgb: "0F3460" } }, fill: { patternType: "solid", fgColor: { rgb: "F0F6FF" } }, alignment: { horizontal: "center", vertical: "center" }, border: { bottom: { style: "thin", color: { rgb: "C5D8F0" } }, top: { style: "thin", color: { rgb: "C5D8F0" } }, left: { style: "thin", color: { rgb: "C5D8F0" } }, right: { style: "thin", color: { rgb: "C5D8F0" } } } },
  pct:          { font: { name: "Arial", sz: 10, bold: true, color: { rgb: "155724" } }, fill: { patternType: "solid", fgColor: { rgb: "D4EDDA" } }, alignment: { horizontal: "center", vertical: "center" }, border: { bottom: { style: "thin", color: { rgb: "C5D8F0" } }, top: { style: "thin", color: { rgb: "C5D8F0" } }, left: { style: "thin", color: { rgb: "C5D8F0" } }, right: { style: "thin", color: { rgb: "C5D8F0" } } } },
  rankBadge:    { font: { name: "Arial", sz: 10, bold: true, color: { rgb: "7B341E" } }, fill: { patternType: "solid", fgColor: { rgb: "FEF3C7" } }, alignment: { horizontal: "center", vertical: "center" }, border: { bottom: { style: "thin", color: { rgb: "C5D8F0" } }, top: { style: "thin", color: { rgb: "C5D8F0" } }, left: { style: "thin", color: { rgb: "C5D8F0" } }, right: { style: "thin", color: { rgb: "C5D8F0" } } } },
  dataEven:     { font: { name: "Arial", sz: 10, color: { rgb: "1A1A2E" } }, fill: { patternType: "solid", fgColor: { rgb: "EEF4FB" } }, alignment: { horizontal: "left", vertical: "center", wrapText: true }, border: { top: { style: "hair", color: { rgb: "C5D8F0" } }, bottom: { style: "hair", color: { rgb: "C5D8F0" } }, left: { style: "thin", color: { rgb: "C5D8F0" } }, right: { style: "thin", color: { rgb: "C5D8F0" } } } },
  dataOdd:      { font: { name: "Arial", sz: 10, color: { rgb: "1A1A2E" } }, fill: { patternType: "solid", fgColor: { rgb: "FFFFFF" } }, alignment: { horizontal: "left", vertical: "center", wrapText: true }, border: { top: { style: "hair", color: { rgb: "C5D8F0" } }, bottom: { style: "hair", color: { rgb: "C5D8F0" } }, left: { style: "thin", color: { rgb: "C5D8F0" } }, right: { style: "thin", color: { rgb: "C5D8F0" } } } },
  highlight:    { font: { name: "Arial", sz: 10, bold: true, color: { rgb: "7C3AED" } }, fill: { patternType: "solid", fgColor: { rgb: "EDE9FE" } }, alignment: { horizontal: "left", vertical: "center" }, border: { bottom: { style: "thin", color: { rgb: "C5D8F0" } }, top: { style: "thin", color: { rgb: "C5D8F0" } }, left: { style: "thin", color: { rgb: "C5D8F0" } }, right: { style: "thin", color: { rgb: "C5D8F0" } } } },
  warning:      { font: { name: "Arial", sz: 10, bold: true, color: { rgb: "92400E" } }, fill: { patternType: "solid", fgColor: { rgb: "FEF3C7" } }, alignment: { horizontal: "center", vertical: "center" }, border: { bottom: { style: "thin", color: { rgb: "C5D8F0" } }, top: { style: "thin", color: { rgb: "C5D8F0" } }, left: { style: "thin", color: { rgb: "C5D8F0" } }, right: { style: "thin", color: { rgb: "C5D8F0" } } } },
  barFill:      { font: { name: "Courier New", sz: 8, bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "2E75B6" } }, alignment: { horizontal: "left", vertical: "center" } },
  areaSubHead:  { font: { name: "Arial", sz: 10, bold: true, color: { rgb: "1E5AA0" } }, fill: { patternType: "solid", fgColor: { rgb: "DBEAFE" } }, alignment: { horizontal: "left", vertical: "center" }, border: { bottom: { style: "thin", color: { rgb: "93C5FD" } } } },
  brandSubHead: { font: { name: "Arial", sz: 10, bold: true, color: { rgb: "0F5132" } }, fill: { patternType: "solid", fgColor: { rgb: "D1FAE5" } }, alignment: { horizontal: "left", vertical: "center" }, border: { bottom: { style: "thin", color: { rgb: "6EE7B7" } } } },
  prodSubHead:  { font: { name: "Arial", sz: 10, bold: true, color: { rgb: "7C3AED" } }, fill: { patternType: "solid", fgColor: { rgb: "EDE9FE" } }, alignment: { horizontal: "left", vertical: "center" }, border: { bottom: { style: "thin", color: { rgb: "C4B5FD" } } } },
  answerCell:   { font: { name: "Arial", sz: 10, color: { rgb: "374151" } }, fill: { patternType: "solid", fgColor: { rgb: "F9FAFB" } }, alignment: { horizontal: "left", vertical: "center", wrapText: true }, border: { top: { style: "hair", color: { rgb: "E5E7EB" } }, bottom: { style: "hair", color: { rgb: "E5E7EB" } }, left: { style: "thin", color: { rgb: "D1D5DB" } }, right: { style: "thin", color: { rgb: "D1D5DB" } } } },
  cartonSubHead: { font: { name: "Arial", sz: 10, bold: true, color: { rgb: "92400E" } }, fill: { patternType: "solid", fgColor: { rgb: "FEF3C7" } }, alignment: { horizontal: "left", vertical: "center" }, border: { bottom: { style: "thin", color: { rgb: "FCD34D" } } } },
};

const sc = (ws, addr, val, style) => {
  if (!ws[addr]) ws[addr] = { v: val === undefined ? "" : val, t: typeof val === "number" ? "n" : "s" };
  else { ws[addr].v = val === undefined ? "" : val; ws[addr].t = typeof val === "number" ? "n" : "s"; }
  if (style) ws[addr].s = style;
};

const writeRow = (ws, rowIdx, cols, startCol = 0) => {
  cols.forEach((col, ci) => {
    const addr  = XLSX.utils.encode_cell({ r: rowIdx, c: startCol + ci });
    const val   = typeof col === "object" && col !== null && "val"   in col ? col.val   : col;
    const style = typeof col === "object" && col !== null && "style" in col ? col.style : undefined;
    sc(ws, addr, val, style);
  });
};

const merge = (ws, r1, c1, r2, c2) => {
  if (!ws["!merges"]) ws["!merges"] = [];
  ws["!merges"].push({ s: { r: r1, c: c1 }, e: { r: r2, c: c2 } });
};

const makeBar = (value, max, width = 20) => {
  const filled = Math.max(0, Math.round((value / Math.max(max, 1)) * width));
  return "█".repeat(filled) + "░".repeat(Math.max(0, width - filled));
};

const sorted = (obj) => Object.entries(obj || {}).sort((a, b) => b[1] - a[1]);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function SurveyResponses() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [responses, setResponses] = useState([]);
  const [regions, setRegions] = useState([]);
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [activeTab, setActiveTab] = useState("table");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const fetchResponses = useCallback(async () => {
    try {
      const url = selectedRegion
        ? `${API_URLS.admin.surveyResponses(surveyId)}?region_id=${selectedRegion}&all=true`
        : `${API_URLS.admin.surveyResponses(surveyId)}?all=true`;
      const resp = await apiRequest(url);
      const responseData = resp.data || [];
      setResponses(Array.isArray(responseData) ? responseData : []);
      if (resp.survey) setSurvey(resp.survey);
    } catch (err) { setError(err.message || "Failed to fetch responses"); }
    finally { setLoading(false); }
  }, [selectedRegion, surveyId]);

  const fetchAllResponses = useCallback(async () => {
    setResponses([]); setError("Please select a specific survey to view responses"); setLoading(false);
  }, []);

  useEffect(() => {
    if (surveyId) fetchResponses(); else fetchAllResponses();
    fetchRegions();
  }, [surveyId, selectedRegion, fetchResponses, fetchAllResponses]);

  const fetchRegions = async () => {
    try { const r = await apiRequest(API_URLS.admin.getRegions); setRegions(r.data || []); }
    catch (e) { console.error(e); }
  };

  const handleRegionFilter = (v) => { setSelectedRegion(v); setLoading(true); };

  // ─── Answer helpers ────────────────────────────────────────────────────────

  /**
   * FIX 1: Outlet name fallback.
   * Prefer the dedicated DB column; fall back to any question answer
   * whose question text contains "outlet name".
   */
  const getOutletName = (resp) => {
    if (resp.outlet_name && resp.outlet_name.trim()) return resp.outlet_name.trim();
    const a = resp.answers?.find(a =>
      a.question?.question?.toLowerCase().includes("outlet name") ||
      a.question?.question?.toLowerCase().includes("outlet nam")
    );
    return a?.answer?.trim() || "—";
  };

  const parseProductList = (raw) => {
    if (!raw || raw === "N/A") return [];
    if (Array.isArray(raw)) return raw.map(s => s.trim()).filter(Boolean);
    const str = String(raw).trim();
    if (str.includes(",")) return str.split(",").map(s => s.trim()).filter(Boolean);
    if (str.includes("\n")) return str.split("\n").map(s => s.trim()).filter(Boolean);
    const tokens = str.split(/\s+/).filter(Boolean);
    const looksLikeList = tokens.length >= 2 && tokens.every(t => t.length <= 20 && !/\d/.test(t));
    if (looksLikeList) return tokens.map(t => t.trim());
    return [str];
  };

  const getAns = (resp, text) => {
    const a = resp.answers?.find(a => a.question?.question?.toLowerCase().includes(text.toLowerCase()));
    return a?.answer || "N/A";
  };

  const getMulti = (resp, text) => {
    const a = resp.answers?.find(a => a.question?.question?.toLowerCase().includes(text.toLowerCase()));
    if (!a) return "N/A";
    if (a?.question?.type === "multiple_select" || Array.isArray(a.answer_array)) {
      const arr = Array.isArray(a.answer_array) ? a.answer_array : [];
      if (arr.length > 0) return arr.map(b => b?.trim()).filter(Boolean).join(", ");
    }
    const parsed = parseProductList(a.answer);
    return parsed.length > 0 ? parsed.join(", ") : "N/A";
  };

  const getProducts = (resp) => {
    const a = resp.answers?.find(a =>
      a.question?.question?.toLowerCase().includes("most selling") ||
      a.question?.question?.toLowerCase().includes("top selling") ||
      a.question?.question?.toLowerCase().includes("best selling")
    );
    if (!a) return [];
    if (Array.isArray(a.answer_array) && a.answer_array.length > 0)
      return a.answer_array.map(s => s?.trim()).filter(Boolean);
    return parseProductList(a.answer);
  };

  const parseNum = (str) => {
    if (!str || str === "N/A") return null;
    const n = parseFloat(String(str).replace(/[^0-9.]/g, ""));
    return isNaN(n) ? null : n;
  };

  /**
   * FIX 2: Carton question detection.
   * Matches any number-type question related to carton/monthly sales.
   */
  const isCartonQuestion = (qText = "", qType = "") => {
    if (qType !== "number") return false;
    const t = qText.toLowerCase();
    return (
      t.includes("carton") ||
      t.includes("average sales") ||
      t.includes("monthly sales") ||
      t.includes("sales per month") ||
      t.includes("sales in carton")
    );
  };

  // ─── Collect all unique question columns across all responses ───────────────
  const getAllQuestionColumns = () => {
    const seen = new Map();
    responses.forEach(resp => {
      resp.answers?.forEach(a => {
        if (a.question && !seen.has(a.question_id)) {
          seen.set(a.question_id, {
            id: a.question_id,
            question: a.question.question,
            type: a.question.type,
            order: a.question.order ?? 9999,
            option_text: a.question.option_text ?? null,
          });
        }
      });
    });
    const sorted = [...seen.values()].sort((a, b) => a.order - b.order);
    console.log('Question columns found:', sorted.length, sorted.map(q => q.question));
    return sorted;
  };

  const getAnswerByQuestionId = (resp, questionId) => {
    const a = resp.answers?.find(a => a.question_id === questionId);
    if (!a) return "";
    if (a.question?.type === "multiple_select" && Array.isArray(a.answer_array) && a.answer_array.length > 0) {
      const result = a.answer_array.join(", ");
      console.log(`Multi-select answer for Q${questionId}:`, result);
      return result;
    }
    console.log(`Answer for Q${questionId}:`, a.answer);
    return a.answer || "";
  };

  // Helper function to extract carton sales data for the special column
  const getCartonSalesData = (resp) => {
    const cartonData = {};
    resp.answers?.forEach(a => {
      const qText = a.question?.question || "";
      const qType = a.question?.type || "";
      if (!isCartonQuestion(qText, qType)) return;

      // Brand name: prefer option_text (set for child questions), else extract from question text
      const brand =
        (a.question?.option_text && a.question.option_text.trim()) ||
        (() => {
          const m = qText.match(/for\s+([A-Za-z0-9']+)/i);
          return m ? m[1] : qText.replace(/[^a-zA-Z0-9\s]/g, "").trim().split(/\s+/).slice(-1)[0] || "Unknown";
        })();

      const val = parseFloat(a.answer);
      if (!isNaN(val) && val >= 0) {
        cartonData[brand] = val;
      }
    });

    // Format as "Brand: X cartons" or return empty if no data
    const entries = Object.entries(cartonData);
    if (entries.length === 0) return "";
    return entries.map(([brand, cartons]) => `${brand}: ${cartons}`).join(", ");
  };

  // ─── Analytics Engine ──────────────────────────────────────────────────────
  const buildAnalytics = () => {
    const brandCounts = {}, categoryCounts = {}, areaCounts = {}, regionCounts = {};
    const enumeratorCounts = {}, productCounts = {}, spendingByRegion = {};
    const brandsByRegion = {}, productsByRegion = {}, areasByRegion = {};
    const spendingByCategory = {}, dailyCounts = {}, outletNames = new Set();
    // FIX 2: carton sales tracking
    const cartonsByBrand = {};        // { brandName: { total, count } }
    const cartonsByBrandByRegion = {}; // { regionName: { brandName: { total, count } } }
    let totalSpending = 0, spendingCount = 0;

    responses.forEach(resp => {
      const regionName = resp.region?.name || getAns(resp, "region") || "Unassigned";
      const area       = resp.area || getAns(resp, "area");
      const category   = getAns(resp, "outlet category");
      const brands     = getMulti(resp, "noodle brands");
      const products   = getProducts(resp);
      const spend      = parseNum(getAns(resp, "average spending"));
      const enumerator = resp.user?.name || resp.enumerator_name || "Anonymous";
      const dateKey    = new Date(resp.created_at).toLocaleDateString();

      // FIX 1: use getOutletName helper
      outletNames.add(getOutletName(resp));
      regionCounts[regionName]   = (regionCounts[regionName] || 0) + 1;
      if (area !== "N/A")     areaCounts[area]         = (areaCounts[area] || 0) + 1;
      if (category !== "N/A") categoryCounts[category] = (categoryCounts[category] || 0) + 1;
      enumeratorCounts[enumerator] = (enumeratorCounts[enumerator] || 0) + 1;
      dailyCounts[dateKey]         = (dailyCounts[dateKey] || 0) + 1;

      if (spend !== null) {
        totalSpending += spend; spendingCount++;
        if (!spendingByRegion[regionName]) spendingByRegion[regionName] = { total: 0, count: 0 };
        spendingByRegion[regionName].total += spend; spendingByRegion[regionName].count++;
        if (!spendingByCategory[category]) spendingByCategory[category] = { total: 0, count: 0 };
        spendingByCategory[category].total += spend; spendingByCategory[category].count++;
      }

      if (brands !== "N/A") brands.split(",").forEach(b => {
        const k = b.trim(); if (!k) return;
        brandCounts[k] = (brandCounts[k] || 0) + 1;
        if (!brandsByRegion[regionName]) brandsByRegion[regionName] = {};
        brandsByRegion[regionName][k] = (brandsByRegion[regionName][k] || 0) + 1;
      });

      products.forEach(p => {
        const k = p.trim(); if (!k) return;
        productCounts[k] = (productCounts[k] || 0) + 1;
        if (!productsByRegion[regionName]) productsByRegion[regionName] = {};
        productsByRegion[regionName][k] = (productsByRegion[regionName][k] || 0) + 1;
      });

      if (!areasByRegion[regionName]) areasByRegion[regionName] = {};
      if (area !== "N/A") areasByRegion[regionName][area] = (areasByRegion[regionName][area] || 0) + 1;

      // FIX 2: collect carton sales per brand
      resp.answers?.forEach(a => {
        const qText = a.question?.question || "";
        const qType = a.question?.type || "";
        if (!isCartonQuestion(qText, qType)) return;

        // Brand name: prefer option_text (set for child questions), else extract from question text
        const brand =
          (a.question?.option_text && a.question.option_text.trim()) ||
          (() => {
            const m = qText.match(/for\s+([A-Za-z0-9']+)/i);
            return m ? m[1] : qText.replace(/[^a-zA-Z0-9\s]/g, "").trim().split(/\s+/).slice(-1)[0] || "Unknown";
          })();

        const val = parseFloat(a.answer);
        if (!isNaN(val) && val >= 0) {
          if (!cartonsByBrand[brand]) cartonsByBrand[brand] = { total: 0, count: 0 };
          cartonsByBrand[brand].total += val;
          cartonsByBrand[brand].count++;

          if (!cartonsByBrandByRegion[regionName]) cartonsByBrandByRegion[regionName] = {};
          if (!cartonsByBrandByRegion[regionName][brand]) cartonsByBrandByRegion[regionName][brand] = { total: 0, count: 0 };
          cartonsByBrandByRegion[regionName][brand].total += val;
          cartonsByBrandByRegion[regionName][brand].count++;
        }
      });
    });

    return {
      brandCounts, categoryCounts, areaCounts, regionCounts,
      enumeratorCounts, productCounts, spendingByRegion,
      brandsByRegion, productsByRegion, areasByRegion,
      spendingByCategory, dailyCounts,
      totalSpending, spendingCount,
      avgSpending: spendingCount > 0 ? totalSpending / spendingCount : 0,
      uniqueOutlets: outletNames.size,
      total: responses.length,
      cartonsByBrand,          // FIX 2
      cartonsByBrandByRegion,  // FIX 2
    };
  };

  // ─── Sorting & Filtering ────────────────────────────────────────────────────
  const sortedFilteredResponses = () => {
    let list = [...responses];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        getOutletName(r).toLowerCase().includes(q) ||
        (r.area || "").toLowerCase().includes(q) ||
        (r.region?.name || "").toLowerCase().includes(q) ||
        (r.user?.name || "").toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      let av, bv;
      if (sortField === "date") { av = new Date(a.created_at); bv = new Date(b.created_at); }
      else if (sortField === "outlet") { av = getOutletName(a).toLowerCase(); bv = getOutletName(b).toLowerCase(); }
      else if (sortField === "region") { av = (a.region?.name || "").toLowerCase(); bv = (b.region?.name || "").toLowerCase(); }
      else if (sortField === "area") { av = (a.area || "").toLowerCase(); bv = (b.area || "").toLowerCase(); }
      else { av = a[sortField]; bv = b[sortField]; }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="opacity-30 ml-0.5">↕</span>;
    return <span className="ml-0.5 text-blue-400">{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  // ─── PDF Download ──────────────────────────────────────────────────────────
  const downloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const pageW = doc.internal.pageSize.getWidth();
    doc.setFillColor(15, 52, 96); doc.rect(0, 0, pageW, 28, "F");
    doc.setTextColor(255, 255, 255); doc.setFontSize(18); doc.setFont("helvetica", "bold");
    doc.text(`Survey Responses: ${survey?.title || "Survey"}`, 14, 12);
    doc.setFontSize(9); doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${new Date().toLocaleString()}   |   Total: ${responses.length}`, 14, 22);
    const headers = ["#", "Outlet", "Region", "Area", "Category", "Brands", "Products", "Spend", "Enumerator", "Date"];
    const cw = [8, 30, 22, 22, 20, 36, 36, 18, 24, 20]; let y = 38;
    doc.setFillColor(30, 90, 160); doc.rect(10, y - 5, cw.reduce((a, b) => a + b, 0), 8, "F");
    doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont("helvetica", "bold");
    let x = 12; headers.forEach((h, i) => { doc.text(h, x, y); x += cw[i]; }); y += 8;
    responses.forEach((resp, idx) => {
      if (y > 190) { doc.addPage(); y = 20; }
      const even = idx % 2 === 0;
      doc.setFillColor(even ? 245 : 255, even ? 248 : 255, even ? 255 : 255);
      doc.rect(10, y - 4, cw.reduce((a, b) => a + b, 0), 7, "F");
      doc.setTextColor(30, 30, 40); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
      const row = [
        String(idx + 1),
        getOutletName(resp), // FIX 1
        resp.region?.name || "—",
        resp.area || getAns(resp, "area"),
        getAns(resp, "outlet category"),
        getMulti(resp, "noodle brands"),
        getProducts(resp).join(", ") || "N/A",
        getAns(resp, "average spending"),
        resp.user?.name || "Anonymous",
        new Date(resp.created_at).toLocaleDateString(),
      ];
      x = 12; row.forEach((cell, i) => { doc.text(String(cell).substring(0, cw[i] / 2.1), x, y); x += cw[i]; }); y += 8;
    });
    const pc = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pc; p++) { doc.setPage(p); doc.setFontSize(8); doc.setTextColor(140, 140, 150); doc.text(`Page ${p} of ${pc}`, pageW - 28, 205); }
    doc.save(`survey-responses-${survey?.title || "survey"}.pdf`);
  };

  // ─── CSV Download ──────────────────────────────────────────────────────────
  const downloadCSV = () => {
    const qCols = getAllQuestionColumns();
    const headers = ["#", "Outlet Name", "Region", "Area", "Enumerator", "Survey Date", "Survey Time", ...qCols.map(q => q.question)];
    const data = responses.map((resp, i) => [
      i + 1,
      getOutletName(resp), // FIX 1
      resp.region?.name || "",
      resp.area || "",
      resp.user?.name || "Anonymous",
      new Date(resp.created_at).toLocaleDateString(),
      new Date(resp.created_at).toLocaleTimeString(),
      ...qCols.map(q => getAnswerByQuestionId(resp, q.id)),
    ]);
    const csv = [headers, ...data].map(row => row.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob), a = document.createElement("a");
    a.href = url; a.download = `survey-responses-${survey?.title || "survey"}.csv`; a.style.visibility = "hidden";
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // EXCEL DOWNLOAD — FULL ANALYTICS WORKBOOK (10 Sheets)
  // ═══════════════════════════════════════════════════════════════════════════
  const downloadExcel = () => {
    const wb = XLSX.utils.book_new();
    const A = buildAnalytics();
    const surveyTitle = survey?.title || "Survey";
    const today = new Date().toLocaleString();
    const dateSlug = new Date().toISOString().split("T")[0];
    const total = A.total;
    const pct = (n) => total > 0 ? `${((n / total) * 100).toFixed(1)}%` : "0.0%";
    const fmtKES = (n) => n != null && !isNaN(n) ? `KES ${Math.round(n).toLocaleString()}` : "N/A";

    // Sheet builder helpers
    const initWs = () => ({ "!merges": [], "!rows": [] });
    const wTitle = (ws, r, text, cols = 6) => {
      merge(ws, r, 0, r, cols - 1);
      sc(ws, XLSX.utils.encode_cell({ r, c: 0 }), text, XL.title);
      ws["!rows"][r] = { hpx: 46 };
    };
    const wSubTitle = (ws, r, text, cols = 6) => {
      merge(ws, r, 0, r, cols - 1);
      sc(ws, XLSX.utils.encode_cell({ r, c: 0 }), text, { font: { name: "Arial", sz: 9, color: { rgb: "666666" } }, alignment: { horizontal: "left", vertical: "center" } });
      ws["!rows"][r] = { hpx: 16 };
    };
    const wSection = (ws, r, text, cols = 6) => {
      merge(ws, r, 0, r, cols - 1);
      sc(ws, XLSX.utils.encode_cell({ r, c: 0 }), text, XL.sectionHead);
      ws["!rows"][r] = { hpx: 26 };
    };
    const wColHeads = (ws, r, heads) => {
      heads.forEach((h, ci) => sc(ws, XLSX.utils.encode_cell({ r, c: ci }), h, XL.colHead));
      ws["!rows"][r] = { hpx: 28 };
    };
    const wDataRow = (ws, r, cells) => {
      const base = r % 2 === 0 ? XL.dataOdd : XL.dataEven;
      cells.forEach((cell, ci) => {
        const addr  = XLSX.utils.encode_cell({ r, c: ci });
        const val   = typeof cell === "object" && cell !== null && "val" in cell ? cell.val : cell;
        const style = typeof cell === "object" && cell !== null && "style" in cell ? cell.style : base;
        sc(ws, addr, val, style);
      });
      ws["!rows"][r] = { hpx: 20 };
    };
    const wSpacer = (ws, r, hpx = 10) => { ws["!rows"][r] = { hpx }; };
    const wSubSection = (ws, r, text, style, cols = 6) => {
      merge(ws, r, 0, r, cols - 1);
      sc(ws, XLSX.utils.encode_cell({ r, c: 0 }), text, style);
      ws["!rows"][r] = { hpx: 22 };
    };
    const finalizeWs = (ws, lastRow, lastCol) => {
      ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: lastRow + 10, c: lastCol } });
    };

    let row = 0;

    // ══════════════════════════════════════════════════════════════════════
    // SHEET 1 — COVER
    // ══════════════════════════════════════════════════════════════════════
    const coverWs = initWs(); row = 0;
    wTitle(coverWs, row, `📊  ${surveyTitle.toUpperCase()} — RESPONSES ANALYTICS REPORT`); row++;
    merge(coverWs, row, 0, row, 5);
    sc(coverWs, "A2", "COMPREHENSIVE SURVEY DATA & ANALYTICS WORKBOOK", { font: { name: "Arial", sz: 12, bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "2E75B6" } }, alignment: { horizontal: "left", vertical: "center" } });
    coverWs["!rows"][row] = { hpx: 28 }; row++;
    wSpacer(coverWs, row, 12); row++;

    const hasCartonData = Object.keys(A.cartonsByBrand).length > 0;

    const infoRows = [
      ["Survey Title",           surveyTitle],
      ["Report Generated",       today],
      ["Total Responses",        total],
      ["Unique Outlets Visited", A.uniqueOutlets],
      ["Regions Covered",        Object.keys(A.regionCounts).length],
      ["Overall Avg Consumer Spend", fmtKES(A.avgSpending)],
      ["Top Noodle Brand",       sorted(A.brandCounts)[0]?.[0] || "N/A"],
      ["Top Selling Product",    sorted(A.productCounts)[0]?.[0] || "N/A"],
      ...(hasCartonData ? [["Top Brand by Avg Carton Sales",
        (() => {
          const top = Object.entries(A.cartonsByBrand).map(([b, d]) => [b, d.count > 0 ? d.total / d.count : 0]).sort((a, b) => b[1] - a[1])[0];
          return top ? `${top[0]} (avg ${Math.round(top[1])} ctn/mo)` : "N/A";
        })()
      ]] : []),
      ["Active Field Agents",    Object.keys(A.enumeratorCounts).length],
      ["Survey Days Active",     Object.keys(A.dailyCounts).length],
      ["Filter Applied",         selectedRegion ? (regions.find(r => r.id === selectedRegion)?.name || "Custom") : "All Regions"],
      ["Total Questions Tracked",getAllQuestionColumns().length],
    ];
    infoRows.forEach(([label, val]) => {
      merge(coverWs, row, 0, row, 1); merge(coverWs, row, 2, row, 5);
      sc(coverWs, XLSX.utils.encode_cell({ r: row, c: 0 }), label, XL.label);
      sc(coverWs, XLSX.utils.encode_cell({ r: row, c: 2 }), val, typeof val === "number" ? XL.numValue : XL.value);
      coverWs["!rows"][row] = { hpx: 24 }; row++;
    });
    wSpacer(coverWs, row, 14); row++;

    wSection(coverWs, row, "📑  WORKBOOK CONTENTS — SHEET INDEX"); row++;
    wColHeads(coverWs, row, ["Sheet", "Description", "", "", "", ""]); row++;
    [
      ["📋 Cover",         "Report overview, key metrics, and sheet guide"],
      ["📊 All Responses", "Raw response data — ALL question columns, frozen header"],
      ["📝 Full Answers",  "Every question × every response in a wide table"],
      ["📈 Summary",       "KPI cards, top brands, top products, carton sales, regional overview"],
      ["🗺️ Regional",     "Per-region breakdown: areas, brands, products, carton sales, spend"],
      ["🏷️ Brands",        "Brand rankings, cross-tabulation by region"],
      ["🛒 Products",      "Top products overall and per region"],
      ["📦 Carton Sales",  "Monthly carton sales by brand — overall and by region"],
      ["💰 Spending",      "Consumer spend analysis by region & category"],
      ["👤 Enumerators",   "Field agent performance and response counts"],
      ["📅 Timeline",      "Daily response trends and cumulative tracking"],
    ].forEach(([sheet, desc], i) => {
      const base = i % 2 === 0 ? XL.dataOdd : XL.dataEven;
      merge(coverWs, row, 1, row, 5);
      sc(coverWs, XLSX.utils.encode_cell({ r: row, c: 0 }), sheet, { ...base, font: { ...base.font, bold: true } });
      sc(coverWs, XLSX.utils.encode_cell({ r: row, c: 1 }), desc, base);
      coverWs["!rows"][row] = { hpx: 22 }; row++;
    });
    coverWs["!cols"] = [{ wch: 26 }, { wch: 4 }, { wch: 35 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
    finalizeWs(coverWs, row, 5);
    XLSX.utils.book_append_sheet(wb, coverWs, "📋 Cover");

    // ══════════════════════════════════════════════════════════════════════
    // SHEET 2 — ALL RESPONSES (fixed columns + dynamic question columns)
    // ══════════════════════════════════════════════════════════════════════
    const qCols = getAllQuestionColumns();
    const FIXED_COLS = ["#", "Outlet Name", "Region", "Area", "Outlet Category", "Enumerator", "Survey Date", "Survey Time", "Avg Carton Sales (Brand: Cartons)"];
    const ALL_COLS = [...FIXED_COLS, ...qCols.map(q => q.question)];

    const respRows = responses.map((resp, i) => {
      const rowObj = {
        "#": i + 1,
        "Outlet Name": getOutletName(resp), // FIX 1
        "Region": resp.region?.name || "",
        "Area": resp.area || "",
        "Outlet Category": getAns(resp, "outlet category"),
        "Enumerator": resp.user?.name || "Anonymous",
        "Survey Date": new Date(resp.created_at).toLocaleDateString(),
        "Survey Time": new Date(resp.created_at).toLocaleTimeString(),
        "Avg Carton Sales (Brand: Cartons)": getCartonSalesData(resp), // Special carton column
      };
      qCols.forEach(q => { rowObj[q.question] = getAnswerByQuestionId(resp, q.id); });
      return rowObj;
    });

    const respWs = XLSX.utils.json_to_sheet(respRows, { header: ALL_COLS });
    respWs["!cols"] = [
      { wch: 5 }, { wch: 28 }, { wch: 18 }, { wch: 20 }, { wch: 18 }, { wch: 22 }, { wch: 14 }, { wch: 14 },
      { wch: 35 }, // Carton sales column width
      ...qCols.map((q, i) => ({ wch: Math.max(25, Math.min(50, q.question?.length * 2)) })),
    ];
    const rRange = XLSX.utils.decode_range(respWs["!ref"]);
    for (let C = rRange.s.c; C <= rRange.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c: C });
      if (respWs[addr]) respWs[addr].s = XL.colHead;
    }
    respWs["!rows"] = [{ hpx: 32 }];
    for (let R = 1; R <= rRange.e.r; R++) {
      respWs["!rows"][R] = { hpx: 22 };
      for (let C = rRange.s.c; C <= rRange.e.c; C++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        if (!respWs[addr]) respWs[addr] = { v: "", t: "s" };
        const base = R % 2 === 0 ? XL.dataEven : XL.dataOdd;
        respWs[addr].s = C === 0
          ? { ...base, font: { ...base.font, bold: true }, alignment: { horizontal: "center", vertical: "center" } }
          : C === 8 ? XL.cartonSubHead // Carton sales column styling
          : C >= FIXED_COLS.length ? XL.answerCell : base;
      }
    }
    respWs["!freeze"] = { xSplit: 2, ySplit: 1, topLeftCell: "C2", activePane: "bottomRight" };
    XLSX.utils.book_append_sheet(wb, respWs, "📊 All Responses");

    // ══════════════════════════════════════════════════════════════════════
    // SHEET 3 — FULL ANSWERS
    // ══════════════════════════════════════════════════════════════════════
    const fullWs = initWs(); row = 0;
    wTitle(fullWs, row, `📝  FULL ANSWER BREAKDOWN — ALL QUESTIONS`, 6); row++;
    wSubTitle(fullWs, row, `Survey: ${surveyTitle}  |  Questions: ${qCols.length}  |  Responses: ${total}`, 6); row++;
    wSpacer(fullWs, row); row++;

    qCols.forEach((q, qi) => {
      wSection(fullWs, row, `Q${qi + 1}:  ${q.question}  [${q.type}]`, 6); row++;
      wColHeads(fullWs, row, ["#", "Outlet", "Region", "Area", "Enumerator", "Answer"]); row++;
      responses.forEach((resp, ri) => {
        const ans = getAnswerByQuestionId(resp, q.id);
        wDataRow(fullWs, row, [
          { val: ri + 1, style: XL.rankBadge },
          { val: getOutletName(resp), style: XL.label }, // FIX 1
          { val: resp.region?.name || "", style: XL.dataOdd },
          { val: resp.area || "", style: XL.dataOdd },
          { val: resp.user?.name || "Anonymous", style: XL.dataOdd },
          { val: ans || "—", style: ans ? XL.answerCell : { ...XL.dataOdd, font: { name: "Arial", sz: 10, color: { rgb: "CCCCCC" } } } },
        ]); row++;
      });
      wSpacer(fullWs, row, 8); row++;
    });

    fullWs["!cols"] = [{ wch: 6 }, { wch: 26 }, { wch: 18 }, { wch: 20 }, { wch: 20 }, { wch: 50 }];
    finalizeWs(fullWs, row, 5);
    XLSX.utils.book_append_sheet(wb, fullWs, "📝 Full Answers");

    // ══════════════════════════════════════════════════════════════════════
    // SHEET 4 — SUMMARY
    // ══════════════════════════════════════════════════════════════════════
    const sumWs = initWs(); row = 0;
    wTitle(sumWs, row, `📈  ${surveyTitle} — SUMMARY ANALYTICS`); row++;
    wSubTitle(sumWs, row, `Generated: ${today}  |  Responses: ${total}  |  Avg Spend: ${fmtKES(A.avgSpending)}  |  Regions: ${Object.keys(A.regionCounts).length}`); row++;
    wSpacer(sumWs, row); row++;

    wSection(sumWs, row, "🎯  KEY PERFORMANCE INDICATORS", 7); row++;
    wColHeads(sumWs, row, ["KPI", "Value", "Status", "Notes", "", "", ""]); row++;
    [
      ["Total Survey Responses",    total,                                                         "✅ Collected",  "All regions combined"],
      ["Unique Outlets Visited",    A.uniqueOutlets,                                               "✅ Captured",   "Distinct outlet names"],
      ["Regions Covered",           Object.keys(A.regionCounts).length,                            "✅ Active",     "Active response regions"],
      ["Questions in Survey",       qCols.length,                                                  "📋 Tracked",   "Unique questions answered"],
      ["Overall Avg Consumer Spend",fmtKES(A.avgSpending),                                         "📊 Tracked",   "Across all categories"],
      ["Top Brand Penetration",     `${sorted(A.brandCounts)[0]?.[0] || "N/A"} (${sorted(A.brandCounts)[0]?.[1] || 0} outlets)`, "🏆 Leader", "Most stocked brand"],
      ["Top Selling Product",       sorted(A.productCounts)[0]?.[0] || "N/A",                      "🏆 Best Seller","Highest mentions"],
      ...(hasCartonData ? [[
        "Top Brand by Avg Carton Sales",
        (() => {
          const top = Object.entries(A.cartonsByBrand).map(([b, d]) => [b, d.count > 0 ? d.total / d.count : 0]).sort((a, b) => b[1] - a[1])[0];
          return top ? `${top[0]} — avg ${Math.round(top[1])} ctn/mo` : "N/A";
        })(),
        "📦 Leader", "Highest avg monthly cartons"
      ]] : []),
      ["Active Field Enumerators",  Object.keys(A.enumeratorCounts).length,                        "👤 Active",    "Unique field agents"],
      ["Survey Days Covered",       Object.keys(A.dailyCounts).length,                             "📅 Days",      "Distinct survey days"],
    ].forEach(([label, val, status, note], ki) => {
      merge(sumWs, row, 3, row, 6);
      wDataRow(sumWs, row, [{ val: label, style: XL.label }, { val: val, style: XL.numValue }, { val: status, style: ki < 3 ? XL.pct : XL.highlight }, { val: note, style: ki % 2 === 0 ? XL.dataOdd : XL.dataEven }, "", "", ""]);
      row++;
    });
    wSpacer(sumWs, row); row++;

    wSection(sumWs, row, "🗺️  REGIONAL OVERVIEW", 7); row++;
    wColHeads(sumWs, row, ["Region", "Responses", "% Share", "Avg Spend (KES)", "Top Brand", "Top Product", "Areas"]); row++;
    sorted(A.regionCounts).forEach(([rName, cnt]) => {
      const rs = A.spendingByRegion[rName];
      const avgS = rs && rs.count > 0 ? rs.total / rs.count : null;
      wDataRow(sumWs, row, [
        { val: rName,   style: XL.label },
        { val: cnt,     style: XL.numValue },
        { val: pct(cnt),style: XL.pct },
        { val: avgS ? Math.round(avgS).toLocaleString() : "N/A", style: avgS ? XL.highlight : XL.dataOdd },
        { val: sorted(A.brandsByRegion[rName] || {})[0]?.[0] || "N/A", style: XL.dataOdd },
        { val: sorted(A.productsByRegion[rName] || {})[0]?.[0] || "N/A", style: XL.dataOdd },
        { val: Object.keys(A.areasByRegion[rName] || {}).length, style: XL.numValue },
      ]); row++;
    });
    wSpacer(sumWs, row); row++;

    wSection(sumWs, row, "🏷️  TOP 10 NOODLE BRANDS (OVERALL)", 7); row++;
    wColHeads(sumWs, row, ["Rank", "Brand", "Outlets", "Coverage %", "In-Cell Bar Chart", "", ""]); row++;
    const maxBC = sorted(A.brandCounts)[0]?.[1] || 1;
    sorted(A.brandCounts).slice(0, 10).forEach(([brand, cnt], bi) => {
      merge(sumWs, row, 4, row, 6);
      wDataRow(sumWs, row, [{ val: bi + 1, style: XL.rankBadge }, { val: brand, style: XL.label }, { val: cnt, style: XL.numValue }, { val: pct(cnt), style: XL.pct }, { val: makeBar(cnt, maxBC, 28), style: XL.barFill }, "", ""]);
      row++;
    });
    wSpacer(sumWs, row); row++;

    wSection(sumWs, row, "🛒  TOP 10 SELLING PRODUCTS (OVERALL)", 7); row++;
    wColHeads(sumWs, row, ["Rank", "Product", "Mentions", "% Share", "In-Cell Bar Chart", "", ""]); row++;
    const maxPC = sorted(A.productCounts)[0]?.[1] || 1;
    sorted(A.productCounts).slice(0, 10).forEach(([prod, cnt], pi) => {
      merge(sumWs, row, 4, row, 6);
      wDataRow(sumWs, row, [{ val: pi + 1, style: XL.rankBadge }, { val: prod, style: XL.label }, { val: cnt, style: XL.numValue }, { val: pct(cnt), style: XL.pct }, { val: makeBar(cnt, maxPC, 28), style: XL.barFill }, "", ""]);
      row++;
    });

    // FIX 2: Carton sales summary in Summary sheet
    if (hasCartonData) {
      wSpacer(sumWs, row); row++;
      wSection(sumWs, row, "📦  AVG MONTHLY CARTON SALES BY BRAND (OVERALL)", 7); row++;
      wColHeads(sumWs, row, ["Rank", "Brand", "Avg Cartons/Month", "Responses w/ Data", "In-Cell Bar Chart", "", ""]); row++;
      const cartonEntries = Object.entries(A.cartonsByBrand)
        .map(([brand, d]) => ({ brand, avg: d.count > 0 ? d.total / d.count : 0, count: d.count }))
        .sort((a, b) => b.avg - a.avg);
      const maxCarton = cartonEntries[0]?.avg || 1;
      cartonEntries.forEach(({ brand, avg, count }, ci) => {
        merge(sumWs, row, 4, row, 6);
        wDataRow(sumWs, row, [
          { val: ci + 1, style: XL.rankBadge },
          { val: brand, style: XL.label },
          { val: Math.round(avg), style: XL.numValue },
          { val: count, style: XL.dataOdd },
          { val: makeBar(avg, maxCarton, 28), style: XL.barFill },
          "", ""
        ]); row++;
      });
    }

    sumWs["!cols"] = [{ wch: 32 }, { wch: 16 }, { wch: 14 }, { wch: 24 }, { wch: 34 }, { wch: 20 }, { wch: 10 }];
    sumWs["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomLeft" };
    finalizeWs(sumWs, row, 6);
    XLSX.utils.book_append_sheet(wb, sumWs, "📈 Summary");

    // ══════════════════════════════════════════════════════════════════════
    // SHEET 5 — REGIONAL DEEP DIVE
    // ══════════════════════════════════════════════════════════════════════
    const regWs = initWs(); row = 0;
    const RCOLS = 7;
    wTitle(regWs, row, "🗺️  REGIONAL ANALYSIS — FULL BREAKDOWN", RCOLS); row++;
    wSubTitle(regWs, row, `Survey: ${surveyTitle}  |  Total: ${total}  |  Regions: ${Object.keys(A.regionCounts).length}`, RCOLS); row++;
    wSpacer(regWs, row); row++;

    sorted(A.regionCounts).forEach(([rName, rTotal]) => {
      const rs = A.spendingByRegion[rName];
      const avgS = rs && rs.count > 0 ? rs.total / rs.count : null;
      wSection(regWs, row, `📍  ${rName.toUpperCase()}   ·   ${rTotal} responses  (${pct(rTotal)})`, RCOLS); row++;
      wColHeads(regWs, row, ["Metric", "Value", "", "Metric", "Value", "", ""]); row++;
      [
        ["Total Responses", rTotal, "Avg Consumer Spend", avgS ? fmtKES(avgS) : "N/A"],
        ["Areas Covered", Object.keys(A.areasByRegion[rName] || {}).length, "Top Noodle Brand", sorted(A.brandsByRegion[rName] || {})[0]?.[0] || "N/A"],
        ["Top Selling Product", sorted(A.productsByRegion[rName] || {})[0]?.[0] || "N/A", "% of Survey Total", pct(rTotal)],
      ].forEach(([l1, v1, l2, v2]) => {
        wDataRow(regWs, row, [
          { val: l1, style: XL.label }, { val: v1, style: XL.numValue }, { val: "", style: XL.dataOdd },
          { val: l2, style: XL.label }, { val: v2, style: XL.numValue }, { val: "", style: XL.dataOdd }, { val: "", style: XL.dataOdd },
        ]); row++;
      });
      wSpacer(regWs, row, 8); row++;

      const areasInReg = sorted(A.areasByRegion[rName] || {});
      if (areasInReg.length > 0) {
        wSubSection(regWs, row, `   📌  GEOGRAPHICAL COVERAGE — ${rName}`, XL.areaSubHead, RCOLS); row++;
        wColHeads(regWs, row, ["Area / Sub-Region", "Responses", "% of Region", "Avg Spend (KES)", "Top Brand", "Top Product", "Bar"]); row++;
        const maxArea = areasInReg[0]?.[1] || 1;
        areasInReg.forEach(([area, aCount]) => {
          const aResps = responses.filter(r => (r.region?.name || "Unassigned") === rName && (r.area || "") === area);
          const aSpends = aResps.map(r => parseNum(getAns(r, "average spending"))).filter(n => n !== null);
          const aAvg = aSpends.length ? aSpends.reduce((a, b) => a + b, 0) / aSpends.length : null;
          const aBrands = {}, aProds = {};
          aResps.forEach(r => {
            getMulti(r, "noodle brands").split(",").forEach(b => { const k = b.trim(); if (k && k !== "N/A") aBrands[k] = (aBrands[k] || 0) + 1; });
            getProducts(r).forEach(p => { const k = p.trim(); if (k) aProds[k] = (aProds[k] || 0) + 1; });
          });
          const regionPct = rTotal > 0 ? `${((aCount / rTotal) * 100).toFixed(1)}%` : "0%";
          wDataRow(regWs, row, [
            { val: area, style: XL.label }, { val: aCount, style: XL.numValue }, { val: regionPct, style: XL.pct },
            { val: aAvg ? Math.round(aAvg).toLocaleString() : "N/A", style: aAvg ? XL.highlight : XL.dataOdd },
            { val: sorted(aBrands)[0]?.[0] || "N/A", style: XL.dataOdd },
            { val: sorted(aProds)[0]?.[0] || "N/A", style: XL.dataOdd },
            { val: makeBar(aCount, maxArea, 16), style: XL.barFill },
          ]); row++;
        });
        wSpacer(regWs, row, 8); row++;
      }

      const brandsInReg = sorted(A.brandsByRegion[rName] || {});
      if (brandsInReg.length > 0) {
        wSubSection(regWs, row, `   🏷️  BRAND AVAILABILITY — ${rName}`, XL.brandSubHead, RCOLS); row++;
        wColHeads(regWs, row, ["Brand", "Outlets Stocking", "% of Region", "Bar Chart", "", "", ""]); row++;
        const maxB = brandsInReg[0]?.[1] || 1;
        brandsInReg.slice(0, 15).forEach(([brand, bCnt]) => {
          merge(regWs, row, 3, row, 6);
          wDataRow(regWs, row, [{ val: brand, style: XL.label }, { val: bCnt, style: XL.numValue }, { val: rTotal > 0 ? `${((bCnt / rTotal) * 100).toFixed(1)}%` : "0%", style: XL.pct }, { val: makeBar(bCnt, maxB, 32), style: XL.barFill }, "", "", ""]); row++;
        });
        wSpacer(regWs, row, 8); row++;
      }

      const prodsInReg = sorted(A.productsByRegion[rName] || {});
      if (prodsInReg.length > 0) {
        wSubSection(regWs, row, `   🛒  TOP SELLING PRODUCTS — ${rName}`, XL.prodSubHead, RCOLS); row++;
        wColHeads(regWs, row, ["Product", "Mentions", "% of Region", "Bar Chart", "", "", ""]); row++;
        const maxP = prodsInReg[0]?.[1] || 1;
        prodsInReg.slice(0, 10).forEach(([prod, pCnt]) => {
          merge(regWs, row, 3, row, 6);
          wDataRow(regWs, row, [{ val: prod, style: XL.label }, { val: pCnt, style: XL.numValue }, { val: rTotal > 0 ? `${((pCnt / rTotal) * 100).toFixed(1)}%` : "0%", style: XL.pct }, { val: makeBar(pCnt, maxP, 32), style: XL.barFill }, "", "", ""]); row++;
        });
        wSpacer(regWs, row, 8); row++;
      }

      // FIX 2: Per-region carton sales breakdown
      const regionCartons = A.cartonsByBrandByRegion[rName] || {};
      const regionCartonEntries = Object.entries(regionCartons)
        .map(([b, d]) => ({ brand: b, avg: d.count > 0 ? d.total / d.count : 0, count: d.count }))
        .sort((a, b) => b.avg - a.avg);
      if (regionCartonEntries.length > 0) {
        wSubSection(regWs, row, `   📦  AVG MONTHLY CARTON SALES — ${rName}`, XL.cartonSubHead, RCOLS); row++;
        wColHeads(regWs, row, ["Brand", "Avg Cartons/Month", "Responses w/ Data", "Bar Chart", "", "", ""]); row++;
        const maxRC = regionCartonEntries[0]?.avg || 1;
        regionCartonEntries.forEach(({ brand, avg, count }) => {
          merge(regWs, row, 3, row, 6);
          wDataRow(regWs, row, [
            { val: brand, style: XL.label },
            { val: Math.round(avg), style: XL.numValue },
            { val: count, style: XL.dataOdd },
            { val: makeBar(avg, maxRC, 32), style: XL.barFill },
            "", "", ""
          ]); row++;
        });
        wSpacer(regWs, row, 8); row++;
      }

      const spendData = A.spendingByRegion[rName];
      if (spendData && spendData.count > 0) {
        wSubSection(regWs, row, `   💰  CONSUMER SPENDING — ${rName}`, { font: { name: "Arial", sz: 10, bold: true, color: { rgb: "0F5132" } }, fill: { patternType: "solid", fgColor: { rgb: "DCFCE7" } }, alignment: { horizontal: "left", vertical: "center" } }, RCOLS); row++;
        const avgSpendR = spendData.total / spendData.count;
        wDataRow(regWs, row, [{ val: "Responses with spend data", style: XL.label }, { val: spendData.count, style: XL.numValue }, { val: "", style: XL.dataOdd }, { val: "Average Spend", style: XL.label }, { val: fmtKES(avgSpendR), style: XL.highlight }, { val: "", style: XL.dataOdd }, { val: "", style: XL.dataOdd }]); row++;
        wDataRow(regWs, row, [{ val: "Total Spend Recorded", style: XL.label }, { val: fmtKES(spendData.total), style: XL.numValue }, { val: "", style: XL.dataOdd }, { val: "vs Overall Avg", style: XL.label }, { val: A.avgSpending > 0 ? `${((avgSpendR / A.avgSpending - 1) * 100).toFixed(1)}%` : "N/A", style: avgSpendR >= A.avgSpending ? XL.pct : XL.warning }, { val: "", style: XL.dataOdd }, { val: "", style: XL.dataOdd }]); row++;
        wSpacer(regWs, row, 8); row++;
      }

      merge(regWs, row, 0, row, RCOLS - 1);
      sc(regWs, XLSX.utils.encode_cell({ r: row, c: 0 }), "", { fill: { patternType: "solid", fgColor: { rgb: "CBD5E1" } } });
      regWs["!rows"][row] = { hpx: 4 }; row++;
      wSpacer(regWs, row, 10); row++;
    });

    regWs["!cols"] = [{ wch: 32 }, { wch: 16 }, { wch: 16 }, { wch: 24 }, { wch: 28 }, { wch: 28 }, { wch: 22 }];
    finalizeWs(regWs, row, RCOLS - 1);
    XLSX.utils.book_append_sheet(wb, regWs, "🗺️ Regional");

    // ══════════════════════════════════════════════════════════════════════
    // SHEET 6 — BRANDS
    // ══════════════════════════════════════════════════════════════════════
    const brandWs = initWs(); row = 0;
    const allRegionNames = Object.keys(A.regionCounts);
    const BCOLS = Math.max(7, allRegionNames.length + 3);
    wTitle(brandWs, row, "🏷️  NOODLE BRAND AVAILABILITY — COMPLETE ANALYSIS", BCOLS); row++;
    wSubTitle(brandWs, row, `Survey: ${surveyTitle}  |  Total Outlets: ${total}  |  Unique Brands: ${Object.keys(A.brandCounts).length}`, BCOLS); row++;
    wSpacer(brandWs, row); row++;

    wSection(brandWs, row, "OVERALL BRAND RANKINGS", BCOLS); row++;
    wColHeads(brandWs, row, ["Rank", "Brand Name", "Outlets Stocking", "Market Coverage %", "In-Cell Chart", "Trophy", ""]); row++;
    const maxBrandCnt = sorted(A.brandCounts)[0]?.[1] || 1;
    sorted(A.brandCounts).forEach(([brand, cnt], bi) => {
      const trophy = bi === 0 ? "🥇 Champion" : bi === 1 ? "🥈 Runner-up" : bi === 2 ? "🥉 Third" : bi < 10 ? `Top ${bi + 1}` : "";
      merge(brandWs, row, 4, row, 6);
      wDataRow(brandWs, row, [
        { val: bi + 1, style: XL.rankBadge }, { val: brand, style: XL.label },
        { val: cnt, style: XL.numValue }, { val: pct(cnt), style: XL.pct },
        { val: makeBar(cnt, maxBrandCnt, 30), style: XL.barFill },
        { val: trophy, style: bi < 3 ? { ...XL.rankBadge, font: { ...XL.rankBadge.font, sz: 12 } } : XL.dataOdd }, "",
      ]); row++;
    });
    wSpacer(brandWs, row); row++;

    wSection(brandWs, row, "BRAND × REGION CROSS-TABULATION (Top 15 Brands)", allRegionNames.length + 3); row++;
    wColHeads(brandWs, row, ["Brand", ...allRegionNames, "Total"]); row++;
    const top15Brands = sorted(A.brandCounts).slice(0, 15).map(([b]) => b);
    top15Brands.forEach((brand) => {
      const cells = [{ val: brand, style: XL.label }];
      let rowTotal = 0;
      allRegionNames.forEach(rName => { const c = (A.brandsByRegion[rName] || {})[brand] || 0; rowTotal += c; cells.push({ val: c > 0 ? c : "—", style: c > 0 ? XL.numValue : XL.dataOdd }); });
      cells.push({ val: rowTotal, style: XL.highlight });
      writeRow(brandWs, row, cells);
      brandWs["!rows"][row] = { hpx: 20 }; row++;
    });
    const totCells = [{ val: "COLUMN TOTALS", style: XL.colHead }];
    allRegionNames.forEach(rName => totCells.push({ val: Object.values(A.brandsByRegion[rName] || {}).reduce((a, b) => a + b, 0), style: { ...XL.colHead, alignment: { horizontal: "center" } } }));
    totCells.push({ val: sorted(A.brandCounts).reduce((a, [, c]) => a + c, 0), style: { ...XL.colHead, alignment: { horizontal: "center" } } });
    writeRow(brandWs, row, totCells);
    brandWs["!rows"][row] = { hpx: 26 }; row++;

    brandWs["!cols"] = [{ wch: 30 }, ...allRegionNames.map(() => ({ wch: 16 })), { wch: 12 }];
    finalizeWs(brandWs, row, allRegionNames.length + 1);
    XLSX.utils.book_append_sheet(wb, brandWs, "🏷️ Brands");

    // ══════════════════════════════════════════════════════════════════════
    // SHEET 7 — PRODUCTS
    // ══════════════════════════════════════════════════════════════════════
    const prodWs = initWs(); row = 0;
    wTitle(prodWs, row, "🛒  TOP SELLING PRODUCTS — COMPLETE ANALYSIS", 7); row++;
    wSubTitle(prodWs, row, `Survey: ${surveyTitle}  |  Unique Products Mentioned: ${Object.keys(A.productCounts).length}`, 7); row++;
    wSpacer(prodWs, row); row++;

    wSection(prodWs, row, "OVERALL PRODUCT RANKINGS", 6); row++;
    wColHeads(prodWs, row, ["Rank", "Product Name", "Mentions", "% Share", "In-Cell Chart", ""]); row++;
    const maxProdCnt = sorted(A.productCounts)[0]?.[1] || 1;
    sorted(A.productCounts).slice(0, 10).forEach(([prod, cnt], pi) => {
      merge(prodWs, row, 4, row, 5);
      wDataRow(prodWs, row, [{ val: pi + 1, style: XL.rankBadge }, { val: prod, style: XL.label }, { val: cnt, style: XL.numValue }, { val: pct(cnt), style: XL.pct }, { val: makeBar(cnt, maxProdCnt, 34), style: XL.barFill }, ""]); row++;
    });
    wSpacer(prodWs, row); row++;

    wSection(prodWs, row, "TOP 5 SELLING PRODUCTS — BY REGION", 6); row++;
    wColHeads(prodWs, row, ["Region", "Rank 1", "Rank 2", "Rank 3", "Rank 4", "Rank 5"]); row++;
    sorted(A.regionCounts).forEach(([rName]) => {
      const topProds = sorted(A.productsByRegion[rName] || {}).slice(0, 5);
      wDataRow(prodWs, row, [
        { val: rName, style: XL.label },
        ...Array(5).fill(0).map((_, i) => ({ val: topProds[i] ? `${topProds[i][0]} (${topProds[i][1]})` : "—", style: topProds[i] ? (i === 0 ? XL.highlight : XL.dataOdd) : { ...(row % 2 === 0 ? XL.dataOdd : XL.dataEven), font: { name: "Arial", sz: 10, color: { rgb: "CCCCCC" } } } }))
      ]); row++;
    });

    prodWs["!cols"] = [{ wch: 35 }, { wch: 32 }, { wch: 32 }, { wch: 32 }, { wch: 32 }, { wch: 32 }];
    finalizeWs(prodWs, row, 5);
    XLSX.utils.book_append_sheet(wb, prodWs, "🛒 Products");

    // ══════════════════════════════════════════════════════════════════════
    // SHEET 8 — CARTON SALES (FIX 2: dedicated sheet)
    // ══════════════════════════════════════════════════════════════════════
    const cartonWs = initWs(); row = 0;
    wTitle(cartonWs, row, "📦  AVERAGE MONTHLY CARTON SALES — COMPLETE ANALYSIS", 7); row++;
    wSubTitle(cartonWs, row, `Survey: ${surveyTitle}  |  Brands Tracked: ${Object.keys(A.cartonsByBrand).length}  |  Total Responses: ${total}`, 7); row++;
    wSpacer(cartonWs, row); row++;

    if (hasCartonData) {
      // Overall rankings
      wSection(cartonWs, row, "OVERALL BRAND CARTON SALES RANKINGS", 7); row++;
      wColHeads(cartonWs, row, ["Rank", "Brand", "Avg Cartons/Month", "Total Cartons Recorded", "Responses w/ Data", "Bar Chart", ""]); row++;
      const cartonRankEntries = Object.entries(A.cartonsByBrand)
        .map(([brand, d]) => ({ brand, avg: d.count > 0 ? d.total / d.count : 0, total: d.total, count: d.count }))
        .sort((a, b) => b.avg - a.avg);
      const maxCartonOverall = cartonRankEntries[0]?.avg || 1;
      cartonRankEntries.forEach(({ brand, avg, total: t, count }, ci) => {
        merge(cartonWs, row, 5, row, 6);
        wDataRow(cartonWs, row, [
          { val: ci + 1, style: XL.rankBadge },
          { val: brand, style: XL.label },
          { val: Math.round(avg), style: XL.numValue },
          { val: Math.round(t), style: XL.highlight },
          { val: count, style: XL.dataOdd },
          { val: makeBar(avg, maxCartonOverall, 30), style: XL.barFill },
          ""
        ]); row++;
      });
      wSpacer(cartonWs, row); row++;

      // Cross-tab: Brand × Region
      wSection(cartonWs, row, "CARTON SALES × REGION CROSS-TABULATION (Avg Cartons/Month)", allRegionNames.length + 3); row++;
      wColHeads(cartonWs, row, ["Brand", ...allRegionNames, "Overall Avg"]); row++;
      cartonRankEntries.forEach(({ brand }) => {
        const cells = [{ val: brand, style: XL.label }];
        let overallSum = 0, overallCount = 0;
        allRegionNames.forEach(rName => {
          const d = (A.cartonsByBrandByRegion[rName] || {})[brand];
          const avg = d && d.count > 0 ? Math.round(d.total / d.count) : null;
          if (d && d.count > 0) { overallSum += d.total; overallCount += d.count; }
          cells.push({ val: avg !== null ? avg : "—", style: avg !== null ? XL.numValue : XL.dataOdd });
        });
        cells.push({ val: overallCount > 0 ? Math.round(overallSum / overallCount) : "—", style: XL.highlight });
        writeRow(cartonWs, row, cells);
        cartonWs["!rows"][row] = { hpx: 20 }; row++;
      });
      wSpacer(cartonWs, row); row++;

      // Per-region detailed breakdown
      wSection(cartonWs, row, "PER-REGION DETAILED BREAKDOWN", 7); row++;
      sorted(A.regionCounts).forEach(([rName]) => {
        const regionCartonEntries = Object.entries(A.cartonsByBrandByRegion[rName] || {})
          .map(([b, d]) => ({ brand: b, avg: d.count > 0 ? d.total / d.count : 0, count: d.count }))
          .sort((a, b) => b.avg - a.avg);
        if (regionCartonEntries.length === 0) return;

        wSubSection(cartonWs, row, `   📍  ${rName}`, XL.cartonSubHead, 7); row++;
        wColHeads(cartonWs, row, ["Brand", "Avg Cartons/Month", "Responses w/ Data", "Bar Chart", "", "", ""]); row++;
        const maxReg = regionCartonEntries[0]?.avg || 1;
        regionCartonEntries.forEach(({ brand, avg, count }) => {
          merge(cartonWs, row, 3, row, 6);
          wDataRow(cartonWs, row, [
            { val: brand, style: XL.label },
            { val: Math.round(avg), style: XL.numValue },
            { val: count, style: XL.dataOdd },
            { val: makeBar(avg, maxReg, 34), style: XL.barFill },
            "", "", ""
          ]); row++;
        });
        wSpacer(cartonWs, row, 8); row++;
      });
    } else {
      merge(cartonWs, row, 0, row, 6);
      sc(cartonWs, XLSX.utils.encode_cell({ r: row, c: 0 }), "⚠️  No carton sales data found in this survey's responses. Ensure questions about monthly carton sales are of type 'number'.", {
        font: { name: "Arial", sz: 11, color: { rgb: "92400E" } },
        fill: { patternType: "solid", fgColor: { rgb: "FEF3C7" } },
        alignment: { horizontal: "left", vertical: "center", wrapText: true },
      });
      cartonWs["!rows"][row] = { hpx: 40 }; row++;
    }

    cartonWs["!cols"] = [{ wch: 32 }, { wch: 20 }, { wch: 24 }, { wch: 22 }, { wch: 20 }, { wch: 34 }, { wch: 10 }];
    finalizeWs(cartonWs, row, 6);
    XLSX.utils.book_append_sheet(wb, cartonWs, "📦 Carton Sales");

    // ══════════════════════════════════════════════════════════════════════
    // SHEET 9 — SPENDING
    // ══════════════════════════════════════════════════════════════════════
    const spendWs = initWs(); row = 0;
    wTitle(spendWs, row, "💰  CONSUMER SPENDING ANALYSIS", 6); row++;
    wSubTitle(spendWs, row, `Survey: ${surveyTitle}  |  Avg Spend: ${fmtKES(A.avgSpending)}  |  Responses with spend data: ${A.spendingCount}`, 6); row++;
    wSpacer(spendWs, row); row++;

    wSection(spendWs, row, "OVERALL SPENDING SUMMARY", 6); row++;
    [
      ["Total Responses With Spend Data", A.spendingCount],
      ["Overall Average Spend (KES)",     Math.round(A.avgSpending).toLocaleString()],
      ["Total Spend Recorded (KES)",      Math.round(A.totalSpending).toLocaleString()],
    ].forEach(([label, val]) => {
      merge(spendWs, row, 1, row, 5);
      wDataRow(spendWs, row, [{ val: label, style: XL.label }, { val: val, style: XL.numValue }, "", "", "", ""]); row++;
    });
    wSpacer(spendWs, row); row++;

    wSection(spendWs, row, "AVERAGE CONSUMER SPENDING BY REGION", 6); row++;
    wColHeads(spendWs, row, ["Region", "Responses w/ Data", "Average Spend (KES)", "vs Overall Avg", "Bar Chart", ""]); row++;
    const allRegSpends = Object.entries(A.spendingByRegion).map(([r, d]) => [r, d.count > 0 ? d.total / d.count : 0, d.count]).sort((a, b) => b[1] - a[1]);
    const maxRegSpend = allRegSpends[0]?.[1] || 1;
    allRegSpends.forEach(([rName, avgS, cnt]) => {
      const vsPct = A.avgSpending > 0 ? `${((avgS / A.avgSpending - 1) * 100).toFixed(1)}%` : "N/A";
      merge(spendWs, row, 4, row, 5);
      wDataRow(spendWs, row, [{ val: rName, style: XL.label }, { val: cnt, style: XL.numValue }, { val: `KES ${Math.round(avgS).toLocaleString()}`, style: XL.highlight }, { val: vsPct, style: avgS >= A.avgSpending ? XL.pct : XL.warning }, { val: makeBar(avgS, maxRegSpend, 28), style: XL.barFill }, ""]); row++;
    });
    wSpacer(spendWs, row); row++;

    wSection(spendWs, row, "AVERAGE CONSUMER SPENDING BY OUTLET CATEGORY", 6); row++;
    wColHeads(spendWs, row, ["Category", "Responses w/ Data", "Average Spend (KES)", "vs Overall Avg", "Bar Chart", ""]); row++;
    const allCatSpends = Object.entries(A.spendingByCategory).map(([c, d]) => [c, d.count > 0 ? d.total / d.count : 0, d.count]).sort((a, b) => b[1] - a[1]);
    const maxCatSpend = allCatSpends[0]?.[1] || 1;
    allCatSpends.forEach(([cat, avgS, cnt]) => {
      const vsPct = A.avgSpending > 0 ? `${((avgS / A.avgSpending - 1) * 100).toFixed(1)}%` : "N/A";
      merge(spendWs, row, 4, row, 5);
      wDataRow(spendWs, row, [{ val: cat, style: XL.label }, { val: cnt, style: XL.numValue }, { val: `KES ${Math.round(avgS).toLocaleString()}`, style: XL.highlight }, { val: vsPct, style: avgS >= A.avgSpending ? XL.pct : XL.warning }, { val: makeBar(avgS, maxCatSpend, 28), style: XL.barFill }, ""]); row++;
    });

    spendWs["!cols"] = [{ wch: 32 }, { wch: 20 }, { wch: 26 }, { wch: 18 }, { wch: 34 }, { wch: 8 }];
    finalizeWs(spendWs, row, 5);
    XLSX.utils.book_append_sheet(wb, spendWs, "💰 Spending");

    // ══════════════════════════════════════════════════════════════════════
    // SHEET 10 — ENUMERATORS
    // ══════════════════════════════════════════════════════════════════════
    const enumWs = initWs(); row = 0;
    wTitle(enumWs, row, "👤  ENUMERATOR PERFORMANCE REPORT", 6); row++;
    wSubTitle(enumWs, row, `Survey: ${surveyTitle}  |  Active Agents: ${Object.keys(A.enumeratorCounts).length}  |  Total Responses: ${total}`, 6); row++;
    wSpacer(enumWs, row); row++;
    wSection(enumWs, row, "FIELD AGENT RESPONSE RANKINGS", 6); row++;
    wColHeads(enumWs, row, ["Rank", "Enumerator Name", "Responses", "% of Total", "Bar Chart", "Performance"]); row++;
    const allEnums = sorted(A.enumeratorCounts);
    const maxEnumCnt = allEnums[0]?.[1] || 1;
    const enumAvg = allEnums.length > 0 ? total / allEnums.length : 0;
    allEnums.forEach(([name, cnt], ei) => {
      const perf = cnt >= enumAvg * 1.5 ? "⭐ High Performer" : cnt >= enumAvg * 0.75 ? "✅ On Target" : "⚠️ Below Average";
      const perfStyle = cnt >= enumAvg * 1.5 ? XL.highlight : cnt >= enumAvg * 0.75 ? XL.pct : XL.warning;
      wDataRow(enumWs, row, [{ val: ei + 1, style: XL.rankBadge }, { val: name, style: XL.label }, { val: cnt, style: XL.numValue }, { val: pct(cnt), style: XL.pct }, { val: makeBar(cnt, maxEnumCnt, 30), style: XL.barFill }, { val: perf, style: perfStyle }]); row++;
    });
    enumWs["!cols"] = [{ wch: 8 }, { wch: 30 }, { wch: 16 }, { wch: 14 }, { wch: 36 }, { wch: 22 }];
    finalizeWs(enumWs, row, 5);
    XLSX.utils.book_append_sheet(wb, enumWs, "👤 Enumerators");

    // ══════════════════════════════════════════════════════════════════════
    // SHEET 11 — TIMELINE
    // ══════════════════════════════════════════════════════════════════════
    const timeWs = initWs(); row = 0;
    wTitle(timeWs, row, "📅  RESPONSE TIMELINE ANALYSIS", 5); row++;
    wSubTitle(timeWs, row, `Survey: ${surveyTitle}  |  Active Days: ${Object.keys(A.dailyCounts).length}  |  Total: ${total}`, 5); row++;
    wSpacer(timeWs, row); row++;
    wSection(timeWs, row, "DAILY RESPONSE COUNTS", 5); row++;
    wColHeads(timeWs, row, ["Date", "Responses", "Cumulative", "% of Total", "Bar Chart"]); row++;

    const sortedDays = Object.entries(A.dailyCounts).sort((a, b) => new Date(a[0]) - new Date(b[0]));
    const maxDayCnt = Math.max(...sortedDays.map(([, c]) => c), 1);
    let cumulative = 0;
    sortedDays.forEach(([dateKey, cnt]) => {
      cumulative += cnt;
      wDataRow(timeWs, row, [{ val: dateKey, style: XL.label }, { val: cnt, style: XL.numValue }, { val: cumulative, style: XL.highlight }, { val: pct(cnt), style: XL.pct }, { val: makeBar(cnt, maxDayCnt, 34), style: XL.barFill }]); row++;
    });
    wSpacer(timeWs, row); row++;
    wDataRow(timeWs, row, [{ val: "TOTAL", style: XL.colHead }, { val: total, style: { ...XL.colHead, alignment: { horizontal: "center" } } }, { val: total, style: { ...XL.colHead, alignment: { horizontal: "center" } } }, { val: "100%", style: { ...XL.colHead, alignment: { horizontal: "center" } } }, { val: "", style: XL.colHead }]); row++;

    timeWs["!cols"] = [{ wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 40 }];
    finalizeWs(timeWs, row, 4);
    XLSX.utils.book_append_sheet(wb, timeWs, "📅 Timeline");

    XLSX.writeFile(wb, `${surveyTitle.replace(/\s+/g, "-")}-analytics-${dateSlug}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // IN-PAGE ANALYTICS TAB
  // ─────────────────────────────────────────────────────────────────────────
  const InPageAnalytics = () => {
    if (responses.length === 0) return (
      <div className="flex flex-col items-center py-16 text-slate-400">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-3">
          <span className="text-2xl">📊</span>
        </div>
        <p className="text-sm font-medium">No data available for analytics</p>
      </div>
    );
    const A = buildAnalytics();

    // SVG Ring Chart
    const RingChart = ({ data, size = 120, thickness = 22 }) => {
      const total = data.reduce((s, d) => s + d.value, 0);
      const r = (size - thickness) / 2;
      const circ = 2 * Math.PI * r;
      let offset = 0;
      const COLORS = ["#3B82F6", "#8B5CF6", "#10B981", "#F59E0B", "#EF4444", "#06B6D4", "#F97316", "#EC4899"];
      const slices = data.slice(0, 8).map((d, i) => {
        const frac = total > 0 ? d.value / total : 0;
        const dash = frac * circ;
        const gap  = circ - dash;
        const el   = { ...d, dash, gap, offset, color: COLORS[i % COLORS.length], frac };
        offset += dash;
        return el;
      });
      return (
        <div className="flex items-center gap-4">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F1F5F9" strokeWidth={thickness} />
            {slices.map((s, i) => (
              <circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
                stroke={s.color} strokeWidth={thickness}
                strokeDasharray={`${s.dash} ${s.gap}`}
                strokeDashoffset={-s.offset + circ * 0.25}
                strokeLinecap="butt"
              />
            ))}
            <text x={size/2} y={size/2 - 6} textAnchor="middle" className="text-xs" fontSize={22} fontWeight="bold" fill="#1E293B">{data.length}</text>
            <text x={size/2} y={size/2 + 12} textAnchor="middle" fontSize={9} fill="#94A3B8">items</text>
          </svg>
          <div className="flex flex-col gap-1.5 min-w-0 flex-1">
            {slices.slice(0, 6).map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                <span className="truncate text-slate-600 flex-1" title={s.label}>{s.label}</span>
                <span className="font-bold text-slate-800 shrink-0">{s.value}</span>
                <span className="text-slate-400 shrink-0 w-10 text-right">{(s.frac * 100).toFixed(0)}%</span>
              </div>
            ))}
            {slices.length > 6 && <p className="text-xs text-slate-400">+{slices.length - 6} more</p>}
          </div>
        </div>
      );
    };

    // Horizontal bar widget
    const BarWidget = ({ items, maxVal, color = "bg-blue-500" }) => (
      <div className="space-y-2 mt-2">
        {items.map(([label, count], i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className="w-24 text-right text-slate-500 text-xs truncate shrink-0" title={label}>{label}</div>
            <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
              <div className={`${color} h-full rounded-full transition-all`} style={{ width: `${(count / Math.max(maxVal, 1)) * 100}%` }} />
            </div>
            <div className="w-7 text-right text-xs font-bold text-slate-700 shrink-0">{count}</div>
          </div>
        ))}
      </div>
    );

    // Sparkline for daily trend
    const Sparkline = ({ data, color = "#3B82F6" }) => {
      if (!data.length) return null;
      const vals = data.map(([, v]) => v);
      const max = Math.max(...vals, 1), min = Math.min(...vals, 0);
      const W = 220, H = 50;
      const pts = vals.map((v, i) => `${(i / Math.max(vals.length - 1, 1)) * W},${H - ((v - min) / (max - min || 1)) * H}`).join(" ");
      return (
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="w-full">
          <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {vals.map((v, i) => (
            <circle key={i} cx={(i / Math.max(vals.length - 1, 1)) * W} cy={H - ((v - min) / (max - min || 1)) * H} r="3" fill={color} />
          ))}
        </svg>
      );
    };

    const dailyEntries = Object.entries(A.dailyCounts).sort((a, b) => new Date(a[0]) - new Date(b[0]));
    const spendEntries = Object.entries(A.spendingByRegion).map(([r, d]) => [r, d.count > 0 ? Math.round(d.total / d.count) : 0]).sort((a, b) => b[1] - a[1]);

    // FIX 2: carton entries for in-page analytics
    const cartonEntries = Object.entries(A.cartonsByBrand)
      .map(([brand, d]) => [brand, d.count > 0 ? Math.round(d.total / d.count) : 0])
      .sort((a, b) => b[1] - a[1]);
    const hasCartons = cartonEntries.length > 0;

    return (
      <div className="space-y-5">
        {/* KPI Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total Responses",  val: A.total,                                                                                  icon: "📋", color: "bg-blue-600",   sub: `${Object.keys(A.dailyCounts).length} survey days` },
            { label: "Unique Outlets",   val: A.uniqueOutlets,                                                                          icon: "🏪", color: "bg-indigo-600", sub: `${Object.keys(A.areaCounts).length} areas` },
            { label: "Avg Consumer Spend", val: A.avgSpending > 0 ? `KES ${Math.round(A.avgSpending).toLocaleString()}` : "N/A",        icon: "💰", color: "bg-emerald-600",sub: `${A.spendingCount} recorded` },
            { label: "Regions Active",   val: Object.keys(A.regionCounts).length,                                                       icon: "🗺️", color: "bg-orange-500",  sub: `${Object.keys(A.enumeratorCounts).length} field agents` },
          ].map((kpi, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3 shadow-sm">
              <div className={`${kpi.color} w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0`}>{kpi.icon}</div>
              <div>
                <p className="text-xl font-extrabold text-slate-900 leading-none">{kpi.val}</p>
                <p className="text-xs font-semibold text-slate-500 mt-0.5">{kpi.label}</p>
                <p className="text-xs text-slate-400 mt-0.5">{kpi.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Row 1: Ring charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-1">🏷️ Noodle Brand Distribution</h3>
            <p className="text-xs text-slate-400 mb-4">Share of outlets stocking each brand</p>
            <RingChart data={sorted(A.brandCounts).map(([label, value]) => ({ label, value }))} />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-1">🏪 Outlet Category Mix</h3>
            <p className="text-xs text-slate-400 mb-4">Breakdown of surveyed outlet types</p>
            <RingChart data={sorted(A.categoryCounts).map(([label, value]) => ({ label, value }))} />
          </div>
        </div>

        {/* Row 2: Bar charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-1">🛒 Top Selling Products</h3>
            <p className="text-xs text-slate-400 mb-2">Mentioned across all outlets</p>
            <BarWidget items={sorted(A.productCounts).slice(0, 10)} maxVal={sorted(A.productCounts)[0]?.[1] || 1} color="bg-violet-500" />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-1">💰 Avg Spend by Region (KES)</h3>
            <p className="text-xs text-slate-400 mb-2">Average consumer spend per region</p>
            <BarWidget items={spendEntries} maxVal={spendEntries[0]?.[1] || 1} color="bg-emerald-500" />
          </div>
        </div>

        {/* FIX 2: Carton sales widget */}
        {hasCartons && (
          <div className="bg-white rounded-xl border border-amber-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-base">📦</span>
              <h3 className="text-sm font-bold text-slate-800">Avg Monthly Carton Sales by Brand</h3>
              <span className="ml-auto text-xs px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full border border-amber-200 font-semibold">
                {cartonEntries.length} brand{cartonEntries.length !== 1 ? "s" : ""}
              </span>
            </div>
            <p className="text-xs text-slate-400 mb-3">Average cartons sold per month — reported by enumerators across all outlets</p>
            <div className="space-y-2">
              {cartonEntries.map(([brand, avg], i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <div className="w-5 flex-shrink-0 text-center">
                    <span className="text-xs font-bold text-amber-700">{i + 1}</span>
                  </div>
                  <div className="w-28 text-right text-slate-600 text-xs truncate shrink-0 font-medium" title={brand}>{brand}</div>
                  <div className="flex-1 bg-amber-50 rounded-full h-3 overflow-hidden border border-amber-100">
                    <div
                      className="bg-amber-500 h-full rounded-full transition-all"
                      style={{ width: `${(avg / Math.max(cartonEntries[0]?.[1] || 1, 1)) * 100}%` }}
                    />
                  </div>
                  <div className="w-16 text-right text-xs font-bold text-amber-800 shrink-0">
                    {avg.toLocaleString()} <span className="font-normal text-slate-400">ctn/mo</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Per-region carton breakdown */}
            {Object.keys(A.cartonsByBrandByRegion).length > 0 && (
              <div className="mt-4 pt-4 border-t border-amber-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">By Region</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(A.cartonsByBrandByRegion).map(([rName, brandsInRegion]) => {
                    const regionCartonList = Object.entries(brandsInRegion)
                      .map(([b, d]) => [b, d.count > 0 ? Math.round(d.total / d.count) : 0])
                      .sort((a, b) => b[1] - a[1]);
                    const maxR = regionCartonList[0]?.[1] || 1;
                    return (
                      <div key={rName} className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                        <p className="text-xs font-bold text-amber-800 mb-2">📍 {rName}</p>
                        {regionCartonList.map(([brand, avg], i) => (
                          <div key={i} className="flex items-center gap-1.5 mb-1.5">
                            <span className="text-xs text-slate-500 w-20 truncate shrink-0">{brand}</span>
                            <div className="flex-1 bg-amber-100 rounded-full h-2">
                              <div className="bg-amber-500 h-full rounded-full" style={{ width: `${(avg / maxR) * 100}%` }} />
                            </div>
                            <span className="text-xs font-bold text-amber-700 w-8 text-right shrink-0">{avg}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Row 3: Region + Enumerators */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-1">🗺️ Responses by Region</h3>
            <p className="text-xs text-slate-400 mb-2">Volume of responses per region</p>
            <BarWidget items={sorted(A.regionCounts)} maxVal={sorted(A.regionCounts)[0]?.[1] || 1} color="bg-blue-500" />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-1">👤 Field Agent Performance</h3>
            <p className="text-xs text-slate-400 mb-2">Responses submitted per enumerator</p>
            <BarWidget items={sorted(A.enumeratorCounts)} maxVal={sorted(A.enumeratorCounts)[0]?.[1] || 1} color="bg-pink-500" />
          </div>
        </div>

        {/* Row 4: Timeline sparkline */}
        {dailyEntries.length > 1 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold text-slate-800">📅 Daily Response Trend</h3>
                <p className="text-xs text-slate-400 mt-0.5">{dailyEntries.length} active survey days · peak {Math.max(...dailyEntries.map(([,v])=>v))} responses/day</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-extrabold text-blue-600">{A.total}</p>
                <p className="text-xs text-slate-400">total</p>
              </div>
            </div>
            <Sparkline data={dailyEntries} color="#3B82F6" />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-slate-400">{dailyEntries[0]?.[0]}</span>
              <span className="text-xs text-slate-400">{dailyEntries[dailyEntries.length - 1]?.[0]}</span>
            </div>
            {/* Daily detail strip */}
            <div className="mt-4 overflow-x-auto">
              <div className="flex gap-2 min-w-max">
                {dailyEntries.map(([date, cnt], i) => {
                  const max = Math.max(...dailyEntries.map(([,v])=>v), 1);
                  const h = Math.max(8, Math.round((cnt / max) * 52));
                  return (
                    <div key={i} className="flex flex-col items-center gap-1 group cursor-default">
                      <div className="text-xs font-bold text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity">{cnt}</div>
                      <div className="w-7 bg-slate-100 rounded-t flex flex-col justify-end" style={{ height: 52 }}>
                        <div className="w-full rounded-t bg-blue-500 transition-all" style={{ height: h }} />
                      </div>
                      <div className="text-[9px] text-slate-400 rotate-45 origin-left whitespace-nowrap">{date}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <div className="relative h-12 w-12">
        <div className="absolute inset-0 rounded-full border-4 border-blue-100" />
        <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
      </div>
      <p className="text-sm text-slate-500 font-medium">Loading responses…</p>
    </div>
  );

  const A_live = responses.length > 0 ? buildAnalytics() : null;
  const displayRows = sortedFilteredResponses();

  return (
    <div className="min-h-screen bg-slate-50">

      {/* TOP NAVBAR */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-20 shadow-sm">
        <div className="max-w-screen-2xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            {surveyId && (
              <button onClick={() => navigate("/surveys")}
                className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-semibold whitespace-nowrap">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
                Back
              </button>
            )}
            <div className="h-5 w-px bg-slate-200 hidden sm:block shrink-0" />
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-slate-900 truncate">{survey ? survey.title : "All Survey Responses"}</h1>
              <p className="text-xs text-slate-400">{new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider hidden md:inline">Export:</span>
            {[
              { label: "PDF",   fn: downloadPDF,   cls: "bg-red-50 text-red-700 border-red-200 hover:bg-red-100",     icon: "M14,2L20,8V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V4A2,2 0 0,1 6,2H14M18,20V9H13V4H6V20H18M9,13H11V11H13V13H15V15H13V17H11V15H9V13Z" },
              { label: "Excel", fn: downloadExcel, cls: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100", icon: "M21.17 3.25Q21.5 3.25 21.76 3.5 22 3.74 22 4.08V19.92Q22 20.26 21.76 20.5 21.5 20.75 21.17 20.75H7.83Q7.5 20.75 7.24 20.5 7 20.26 7 19.92V17H2.83Q2.5 17 2.24 16.76 2 16.5 2 16.17V7.83Q2 7.5 2.24 7.24 2.5 7 2.83 7H7V4.08Q7 3.74 7.24 3.5 7.5 3.25 7.83 3.25M7 13.06L8.18 15.28H9.97L8 12.06 9.93 8.89H8.22L7 11.17 5.78 8.89H4.03L5.97 12.06 4 15.28H5.78M15.17 19.25V17H10.83V19.25M15.17 15.5V12.63H10.83V15.5M15.17 11.13V8.25H10.83V11.13M20.67 19.25V17H16.67V19.25M20.67 15.5V12.63H16.67V15.5M20.67 11.13V8.25H16.67V11.13Z" },
              { label: "CSV",   fn: downloadCSV,   cls: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100",   icon: "M14,2H6C4.9,2,4,2.9,4,4v16c0,1.1,0.9,2,2,2h12c1.1,0,2-0.9,2-2V8L14,2z M16,18H8v-2h8V18z M16,14H8v-2h8V14z M13,9V3.5L18.5,9H13z" },
            ].map(btn => (
              <button key={btn.label} onClick={btn.fn} disabled={responses.length === 0}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${btn.cls}`}>
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d={btn.icon}/></svg>
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-5 space-y-5">
        {error && <Alert type="error" message={error} onClose={() => setError("")} />}

        {/* STAT CARDS */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: "Responses",      val: responses.length,                                                                                                             icon: "📋", color: "bg-blue-600" },
            { label: "Regions",        val: regions.length,                                                                                                               icon: "🗺️", color: "bg-indigo-600" },
            { label: "Unique Outlets", val: A_live?.uniqueOutlets || 0,                                                                                                   icon: "🏪", color: "bg-sky-600" },
            { label: "Avg Spend",      val: A_live && A_live.avgSpending > 0 ? `KES ${Math.round(A_live.avgSpending).toLocaleString()}` : "N/A",                         icon: "💰", color: "bg-emerald-600" },
            { label: "Top Brand",      val: A_live ? (Object.entries(A_live.brandCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "N/A") : "N/A",                        icon: "🏷️", color: "bg-orange-500" },
            { label: "Field Agents",   val: A_live ? Object.keys(A_live.enumeratorCounts).length : 0,                                                                    icon: "👤", color: "bg-violet-600" },
          ].map((c, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3 flex items-center gap-3">
              <div className={`${c.color} w-9 h-9 rounded-lg flex items-center justify-center text-base shrink-0`}>{c.icon}</div>
              <div className="min-w-0">
                <p className="text-base font-bold text-slate-900 truncate">{c.val}</p>
                <p className="text-xs text-slate-400">{c.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* FILTER BAR */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Filter by Region</label>
              <div className="relative">
                <select value={selectedRegion} onChange={e => handleRegionFilter(e.target.value)}
                  className="pl-3 pr-8 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer min-w-[180px]">
                  <option value="">All Regions</option>
                  {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                </div>
              </div>
            </div>
            {selectedRegion && <button onClick={() => handleRegionFilter("")} className="text-xs text-blue-600 hover:text-blue-800 font-semibold pb-2">× Clear</button>}
            <div className="flex-1" />
            {/* Search */}
            {activeTab === "table" && (
              <div className="relative">
                <svg className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search outlets, areas…"
                  className="pl-9 pr-8 py-2 text-sm border border-slate-300 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition w-56" />
                {search && <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs">✕</button>}
              </div>
            )}
            <div className="flex items-center gap-1.5 pb-0.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
              <span className="text-sm text-slate-500">
                <strong className="text-slate-800">{displayRows.length}</strong>
                {search ? ` of ${responses.length}` : ""} responses
              </span>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          {[{ id: "table", label: "📋 Data Table" }, { id: "analytics", label: "📈 Analytics" }].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${activeTab === tab.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* DATA TABLE */}
        {activeTab === "table" && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
                Response Records
              </h2>
              <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full font-semibold border border-blue-100">{displayRows.length} records</span>
            </div>
            {displayRows.length === 0 ? (
              <div className="flex flex-col items-center py-16 gap-2 text-slate-400">
                <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                <p className="text-sm font-medium">{search ? `No results for "${search}"` : "No responses found"}</p>
                {search && <button onClick={() => setSearch("")} className="text-xs text-blue-600 hover:underline">Clear search</button>}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      {[
                        { label: "#", field: null },
                        { label: "Outlet Name", field: "outlet" },
                        { label: "Region", field: "region" },
                        { label: "Area", field: "area" },
                        { label: "Category", field: null },
                        { label: "Noodle Brands", field: null },
                        { label: "Top Products", field: null },
                        { label: "Avg Spend", field: null },
                        { label: "Enumerator", field: null },
                        { label: "Time", field: null },
                        { label: "Date", field: "date" },
                      ].map(({ label, field }) => (
                        <th key={label}
                          onClick={() => field && handleSort(field)}
                          className={`px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${field ? "cursor-pointer hover:text-slate-800 select-none" : ""}`}>
                          {label}{field && <SortIcon field={field} />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {displayRows.map((resp, idx) => (
                      <tr key={resp.id} className={`transition-colors hover:bg-blue-50/40 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/30"}`}>
                        <td className="px-3 py-2.5 text-center">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-xs font-bold text-slate-600">{idx + 1}</span>
                        </td>
                        <td className="px-3 py-2.5 font-semibold text-slate-900 whitespace-nowrap max-w-[160px] truncate" title={getOutletName(resp)}>
                          {getOutletName(resp)} {/* FIX 1 */}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                            {resp.region?.name || "—"}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">{resp.area || "—"}</td>
                        <td className="px-3 py-2.5">
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 whitespace-nowrap">
                            {getAns(resp, "outlet category")}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 max-w-[160px]">
                          <div className="flex flex-wrap gap-0.5">
                            {(() => {
                              const bt = getMulti(resp, "noodle brands");
                              if (bt === "N/A") return <span className="text-slate-300 text-xs">N/A</span>;
                              return bt.split(",").map(b => b.trim()).filter(Boolean).slice(0, 4).map((brand, i) => (
                                <span key={i} className="px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-100 font-medium whitespace-nowrap">{brand}</span>
                              ));
                            })()}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-slate-600 max-w-[160px]">
                          {(() => {
                            const prods = getProducts(resp);
                            if (!prods.length) return <span className="text-slate-300 text-xs">N/A</span>;
                            return (
                              <div className="flex flex-wrap gap-0.5">
                                {prods.slice(0, 3).map((p, i) => (
                                  <span key={i} className="px-1.5 py-0.5 rounded text-xs bg-emerald-50 text-emerald-700 border border-emerald-100 font-medium whitespace-nowrap">{p}</span>
                                ))}
                                {prods.length > 3 && <span className="px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-500 font-medium">+{prods.length - 3}</span>}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-0.5">
                            {getAns(resp, "average spending")}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap font-medium">
                          {resp.user?.name || "Anonymous"}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-slate-400 whitespace-nowrap">
                          {new Date(resp.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-slate-400 whitespace-nowrap">
                          {new Date(resp.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === "analytics" && <InPageAnalytics />}
      </div>
    </div>
  );
}